import { TYPES_EXECUTION_MATERIALS, CUBAGE_BUNDLE_STATUS } from '../constants/cubage'

/**
 * "Solicitud de llenado inicial" number of a cubicador v2 bundle, e.g. SLI-000123.
 * ms-cubicador sends it as sli_code; the fallback mirrors its format_sli_code.
 */
export const getSliCode = (bundle) => {
  if (!bundle) return ''
  return bundle.sli_code || `SLI-${String(bundle.id).padStart(6, '0')}`
}

/**
 * One status for the whole request, from the bundle (ms-cubicador) and its SAP documents'
 * progress (ms-entregas /api/promotion/bundle_status).
 */
export const getRequestStatus = (bundle, bundleStatus) => {
  if (!bundle) return null
  if (bundle.status === CUBAGE_BUNDLE_STATUS.PENDING_APPROVAL) {
    return { name: 'En aprobación', color: 'yellow darken-4' }
  }
  if (bundle.status === CUBAGE_BUNDLE_STATUS.REJECTED) {
    return { name: 'Rechazada', color: 'red darken-2' }
  }
  if (bundle.status === CUBAGE_BUNDLE_STATUS.SUPERSEDED) {
    return { name: 'Reemplazada por un nuevo envío', color: 'grey darken-1' }
  }
  if (bundle.status === CUBAGE_BUNDLE_STATUS.CANCELLED) {
    return { name: 'Anulada por reinicio de la cubicación', color: 'grey darken-1' }
  }
  const transfer = bundleStatus && bundleStatus.transfers.length ? bundleStatus.transfers[0] : null
  const hasPurchase = Boolean(bundleStatus && bundleStatus.purchases.length)
  if (transfer && transfer.promotion_status === 'cancelled') {
    return { name: 'Anulada', color: 'grey darken-1' }
  }
  if (transfer && transfer.promotion_status === 'promoted') {
    return { name: 'Traslado generado: en preparación de despacho', color: 'green darken-2' }
  }
  if (transfer && hasPurchase && !transfer.shortfall_fulfilled) {
    return { name: 'Pendiente por compra', color: 'orange darken-2' }
  }
  if (transfer && transfer.fifo_position) {
    return {
      name: `Reservada: en cola de despacho (posición ${transfer.fifo_position} de ${transfer.fifo_queue_length})`,
      color: 'blue darken-2',
    }
  }
  return { name: 'Enviada a SAP', color: 'blue darken-2' }
}

/** Status of one purchase request line from its ordered/received quantities. */
export const getPurchaseLineStatus = (line) => {
  if (line.cancelled) {
    return { name: 'Anulada', color: 'grey darken-1' }
  }
  if (line.quantity && line.received_quantity >= line.quantity) {
    return { name: 'Recibido', color: 'green darken-2' }
  }
  if (line.received_quantity > 0) {
    return { name: 'Recibido parcial', color: 'teal darken-1' }
  }
  if (line.ordered_quantity > 0) {
    return { name: 'Orden de compra emitida', color: 'blue darken-2' }
  }
  return { name: 'Pendiente de compra', color: 'orange darken-2' }
}

const PAYMENT_STATUS = {
  none: { name: 'Sin factura', color: 'grey darken-1' },
  invoiced: { name: 'Facturada, pendiente de pago', color: 'orange darken-2' },
  partially_paid: { name: 'Pago parcial', color: 'teal darken-1' },
  paid: { name: 'Pagada', color: 'green darken-2' },
}

/** Payment status of a purchase line from ms-entregas /api/promotion/bundle_invoices. */
export const getPaymentStatus = (status) => PAYMENT_STATUS[status] || PAYMENT_STATUS.none

// Same business unit -> flag mapping ms-cubicador uses to list standard materials
// (crud/materials.py filter_materials)
const STANDARD_FLAG_BY_BUSINESS_UNIT = {
  RE: 'favorite_re',
  CI: 'favorite_ci',
  PG: 'favorite_pg',
}

/**
 * Whether a material is standard for the project's business unit (SAP code: RE, CI, PG,
 * anything else = otros). Without a business unit (e.g. legacy non-SAP cubicaciones) any
 * standard flag counts, as before.
 */
export const isStandardMaterial = (material, businessUnitCode) => {
  if (!material) return false
  if (businessUnitCode) {
    const flag = STANDARD_FLAG_BY_BUSINESS_UNIT[businessUnitCode] || 'favorite_otros'
    return Boolean(material[flag])
  }
  return Boolean(
    material.favorite ||
      material.favorite_re ||
      material.favorite_ci ||
      material.favorite_pg ||
      material.favorite_otros
  )
}

export const getMappedDocumentMaterial = (documentMaterial) => {
  let available_stock = documentMaterial.stock;
  return {
    ...documentMaterial,
    total_cost:
      documentMaterial.quantity && documentMaterial.cost
        ? documentMaterial.quantity * documentMaterial.cost
        : 0,
    available_stock: available_stock >= 0 ? available_stock : 0,
    material: documentMaterial.material
      ? {
          ...documentMaterial.material,
          unit_cost: documentMaterial.cost,
          stock: documentMaterial.material.stocks.length
            ? documentMaterial.material.stocks[documentMaterial.material.stocks.length - 1]
                .quantity
            : 0
        }
      : {
          ...documentMaterial.custommaterial,
          code: '-',
          unit_cost: documentMaterial.cost,
          stock: 0,
          favorite: false
        }
  }
}

const calculateTotalCostMaterialExecution = (item) => {
    let total = 0
    if(item.data){
      switch (item.service_type) {
        case TYPES_EXECUTION_MATERIALS.transport:
          total = item.data.Unit_Cost * item.data.Quantity * item.data.Days
          break
        case TYPES_EXECUTION_MATERIALS.load_transport:
          total = item.data.Unit_Cost * item.data.Quantity
          break
        case TYPES_EXECUTION_MATERIALS.machinery:
          total = item.data.Unit_Cost * item.data.Quantity * item.data.Hour * item.data.Day_Month
          break
        case TYPES_EXECUTION_MATERIALS.labor:
          total = item.data.Unit_Cost * item.data.Staff * item.data.Days
          break  
        case TYPES_EXECUTION_MATERIALS.lodging:
          total = item.data.Unit_Cost * item.data.Staff * item.data.Days
          break
        case TYPES_EXECUTION_MATERIALS.others:
          total = item.data.Unit_Cost * item.data.Quantity
          break  
        default:
          break
      }
    }
    return total
}
export const getMappedMaterialExecution = (item) => {
  return {
    ...item,
    ...item.data,
    total_cost: !item.pre_created ? calculateTotalCostMaterialExecution(item) : null
  }
}

export const getMappedCubageOtherAttachment = (item) => {
  return {
    ...item,
    total_cost: item.unit_cost * item.quantity
  }
}

export const getMappedCubageBoardPanels = (boardItem) => {
  return {
    ...boardItem,
    unit_cost: boardItem.panel.cost,
    total_cost:
      boardItem.quantity && boardItem.panel.cost
        ? boardItem.quantity * boardItem.panel.cost
        : 0
  }
}
/** dd/mm hh:mm of an ISO datetime from the backends, always in Chile time (not the
 * browser's time zone, which may differ). */
export const formatDateTimeCl = (isoValue) =>
  isoValue
    ? new Date(isoValue).toLocaleString('es-CL', {
        timeZone: 'America/Santiago',
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-'

const formatDayCl = (date) => {
  const parts = new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).formatToParts(date)
  const get = (type) => (parts.find((part) => part.type === type) || {}).value
  return `${get('weekday').replace('.', '')} ${get('day')}/${get('month')}`
}

/** "mié 28/10": the Chile day picking starts (T-48h), from entrega's picking_from. */
export const formatPickingDayCl = (isoValue) => (isoValue ? formatDayCl(new Date(isoValue)) : '-')

/** "mar 27/10": last Chile day a request can be edited, the day before picking starts. */
export const formatEditableUntilCl = (isoValue) =>
  isoValue ? formatDayCl(new Date(new Date(isoValue).getTime() - 1)) : '-'

/** Earliest dispatch date (YYYY-MM-DD): today + 2 business days, same rule as ms-cubicador. */
export const minDispatchIsoDate = (today = new Date()) => {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  let counted = 0
  while (counted < 2) {
    day.setDate(day.getDate() + 1)
    if (day.getDay() !== 0 && day.getDay() !== 6) counted += 1
  }
  const pad = (value) => String(value).padStart(2, '0')
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`
}

export const escapeHtml = (value) =>
  String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const formatIsoDate = (value) => (value ? value.split('-').reverse().join('/') : '-')

/**
 * Body of the confirmation shown when ms-cubicador answers 409 with detail.type ===
 * 'lead_time': purchase lines whose lead time doesn't fit before the desired dispatch date.
 */
export const leadTimeWarningHtml = (detail) => {
  const rows = (detail.items || [])
    .map(
      (item) =>
        `<tr><td>${escapeHtml(item.code)}</td><td class="text-left">${escapeHtml(item.description)}</td><td>${escapeHtml(item.quantity)}</td><td>${escapeHtml(item.lead_time)} días</td><td>${formatIsoDate(item.earliest_date)}</td></tr>`
    )
    .join('')
  const withoutLeadTime = (detail.without_lead_time || []).length
    ? `<p class="mt-3 mb-0"><small>Sin tiempo de entrega cargado en SAP (no se pudieron revisar): ${detail.without_lead_time.map(escapeHtml).join(', ')}</small></p>`
    : ''
  return `
    <p>${escapeHtml(detail.message)}:</p>
    <table class="table table-sm">
      <thead><tr><th>Código</th><th>Descripción</th><th>Cantidad a comprar</th><th>Tiempo de entrega</th><th>Llegaría el</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    ${withoutLeadTime}
    <small>Si deseas cambiar la fecha o los materiales presiona Cancelar, si deseas continuar igualmente presiona Aceptar</small>
  `
}

// item_gasto codes of main equipment (same list as ms-cubicador MAIN_EQUIPMENT_ITEM_GASTO)
export const MAIN_EQUIPMENT_ITEM_GASTO = ['EXIST01', 'EXIST02', 'EXIST05', 'EXIST06', 'EXIST07', 'EXIST08']

const toIsoDate = (date) => {
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Estimated arrival of a purchase line (ms-entregas bundle_status line): its PO delivery date
 * if it has one, else today + the material's SAP lead time. late = after desiredDate
 * (YYYY-MM-DD). null for cancelled lines.
 */
export const getPurchaseLineArrival = (line, leadTimeDays, desiredDate, today = new Date()) => {
  if (line.cancelled) return null
  if (line.quantity && line.received_quantity >= line.quantity) {
    return { date: null, label: 'Recibido', color: 'green darken-2', late: false, source: null }
  }
  let date = null
  let source = null
  if (line.delivery_date) {
    date = String(line.delivery_date).slice(0, 10)
    source = 'OC'
  } else if (leadTimeDays) {
    const estimate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + Number(leadTimeDays))
    date = toIsoDate(estimate)
    source = 'tiempo de entrega'
  }
  if (!date) {
    return { date: null, label: 'Sin fecha estimada', color: 'grey darken-1', late: false, source: null }
  }
  const late = Boolean(desiredDate && date > desiredDate)
  return {
    date,
    label: date.split('-').reverse().join('/'),
    color: late ? 'red darken-2' : 'green darken-2',
    late,
    source,
  }
}
